import { Button, Chip, Table, Tooltip } from '@heroui/react'

import { PencilIcon, PlusIcon } from '@/components/ui/icons'

import type { UnitCatalog } from '../hooks'
import type { Recipe, StockItem } from '../types'
import { RecipeStatus, StockItemStatus } from '../types'
import { recipeCost, recipeCostOf, recipeQuantity, recipeStatusLabels } from './inventory-format'

interface RecipesTableProps {
  recipes: Recipe[]
  /** The items the recipes make, by id — for their names. */
  items: ReadonlyMap<string, StockItem>
  /** The recipes' ingredients, by id — for what each recipe costs. */
  ingredients: ReadonlyMap<string, StockItem>
  /** The active units, for converting a line into its ingredient's unit. */
  units: UnitCatalog
  /** Opens the recipe's page. */
  onOpen: (recipe: Recipe) => void
}

/**
 * One row per recipe, with the item it makes and what it costs to make.
 *
 * A draft reads as what it is — an empty shell waiting for its ingredients —
 * rather than as a recipe with zero lines, and its button offers "Add
 * ingredients" because that is the errand: filling it in is what puts it to use.
 *
 * The whole row opens the recipe's page on one click, the same as its button:
 * there is no selection here for a click to mean instead. A row whose item has
 * not loaded, or is archived, stays put, as its button does.
 */
export function RecipesTable({ recipes, items, ingredients, units, onOpen }: RecipesTableProps) {
  /** An archived item's recipe cannot be changed, so it is not offered for editing. */
  function canEdit(recipe: Recipe) {
    const item = items.get(recipe.stockItemId)
    return Boolean(item) && item?.status !== StockItemStatus.Archived
  }

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content
          aria-label="Recipes"
          onRowAction={(key) => {
            const recipe = recipes.find((row) => row.id === String(key))
            if (recipe && canEdit(recipe)) onOpen(recipe)
          }}
        >
          <Table.Header>
            <Table.Column id="recipe" isRowHeader>
              Recipe
            </Table.Column>
            <Table.Column id="item">Makes</Table.Column>
            <Table.Column id="yield">Yield</Table.Column>
            <Table.Column id="lines">Deducts</Table.Column>
            <Table.Column id="cost">Recipe cost</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="actions">
              <span className="sr-only">Actions</span>
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {recipes.map((recipe) => {
              const item = items.get(recipe.stockItemId)
              const isDraft = recipe.status === RecipeStatus.Draft
              const actionLabel = isDraft ? 'Add ingredients' : 'Edit recipe'

              return (
                <Table.Row
                  key={recipe.id}
                  id={recipe.id}
                  className={canEdit(recipe) ? 'cursor-pointer' : undefined}
                >
                  <Table.Cell>
                    <span className="flex flex-col">
                      <span className="font-medium">{recipe.name}</span>
                      <span className="text-muted text-xs tabular-nums">v{recipe.version}</span>
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    {item ? (
                      <span className="flex flex-col">
                        <span>{item.name}</span>
                        <span className="text-muted text-xs">{item.sku}</span>
                      </span>
                    ) : (
                      <span className="text-muted">…</span>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    <span className="tabular-nums">
                      {recipeQuantity(recipe.yieldQty)} {recipe.yieldUnit}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <span className={`tabular-nums ${recipe.lines.length ? '' : 'text-muted'}`}>
                      {recipe.lines.length || 'None yet'}
                    </span>
                  </Table.Cell>

                  <Table.Cell>
                    <CostCell recipe={recipe} ingredients={ingredients} units={units} />
                  </Table.Cell>

                  <Table.Cell>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={
                        recipe.status === RecipeStatus.Active
                          ? 'success'
                          : isDraft
                            ? 'warning'
                            : 'default'
                      }
                    >
                      {recipeStatusLabels[recipe.status]}
                    </Chip>
                  </Table.Cell>

                  <Table.Cell>
                    <span className="flex justify-end">
                      <Tooltip delay={300}>
                        <Button
                          variant="ghost"
                          size="sm"
                          isIconOnly
                          isDisabled={!canEdit(recipe)}
                          aria-label={`${actionLabel}: ${recipe.name}`}
                          onPress={() => onOpen(recipe)}
                          className="text-muted hover:text-foreground size-8 rounded-full"
                        >
                          {isDraft ? (
                            <PlusIcon width={16} height={16} />
                          ) : (
                            <PencilIcon width={16} height={16} />
                          )}
                        </Button>
                        <Tooltip.Content>{actionLabel}</Tooltip.Content>
                      </Tooltip>
                    </span>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}

/**
 * What one run of the recipe costs, and per unit of what it makes when that is
 * more than one. A dash for a recipe with no ingredients, an ellipsis while its
 * ingredients load, and a note when some have no cost yet — the total leaves
 * those out, so it would otherwise read as cheaper than it is.
 */
function CostCell({
  recipe,
  ingredients,
  units,
}: {
  recipe: Recipe
  ingredients: ReadonlyMap<string, StockItem>
  units: UnitCatalog
}) {
  if (recipe.lines.length === 0) return <span className="text-muted">—</span>

  const { total, uncosted, pending } = recipeCostOf(recipe, ingredients, units)
  if (pending > 0) return <span className="text-muted">…</span>

  return (
    <span className="flex flex-col tabular-nums">
      <span className={uncosted === recipe.lines.length ? 'text-muted' : 'font-medium'}>
        {recipeCost(total)}
      </span>
      {uncosted > 0 ? (
        <span className="text-warning text-xs">{uncosted} without cost</span>
      ) : (
        recipe.yieldQty !== 1 &&
        recipe.yieldQty > 0 && (
          <span className="text-muted text-xs">
            {recipeCost(total / recipe.yieldQty)} / {recipe.yieldUnit}
          </span>
        )
      )}
    </span>
  )
}
